import React, {useCallback, useEffect, useState} from "react"
import {formatTextTestIds} from "@next/utils"
import connect from "./connect"
import env from "../../config/env"
import {featAvailable, featBackInStock, featDeliverBy} from "../../config/constants"
import {
    FacetCheckbox,
    FacetFormControl,
    FacetWrapper,
    FacetTooltipIcon,
    HiddenNoTranslationFacetValue,
    FacetCountLabel,
} from "./components"
import {FacetState} from "../../models/FacetsState"
import TooltipComponent from "../facetTooltip"
import noTranslationText from "../../utils/noTranslationText"

interface FacetProps {
    handleSetFacet: (facetValue: string) => void
    facet: FacetState
    enabledTooltips: boolean
    facetTooltipHandler?: (facetValue: string) => void
    facetValueTooltipOpen?: string
    modal?: boolean
    innerRef?: React.RefObject<HTMLInputElement>
}

export const Facet = ({
    facet = {} as any,
    handleSetFacet,
    enabledTooltips,
    facetTooltipHandler = () => null,
    facetValueTooltipOpen = "",
    modal = false,
    innerRef,
}: FacetProps) => {
    const [visibleTooltip, setVisibleTooltip] = useState<boolean>(false)
    const {v, s, c, n, d} = facet
    useEffect(() => {
        if (v === featAvailable || v === featBackInStock || v === featDeliverBy) {
            setVisibleTooltip(true)
        }
    }, [v])

    const handleTooltipClose = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        facetTooltipHandler("")
        event.stopPropagation()
        event.preventDefault()
    }

    const handleTooltipOpen = (event: any) => {
        if (event.key === "Enter" || event.type === "click") {
            facetTooltipHandler(v)
            event.stopPropagation()
            event.preventDefault()
        }
    }

    const handleChange = useCallback(() => {
        handleSetFacet(v)
    }, [handleSetFacet, v])

    const handleKeyDown = useCallback(
        e => {
            if (e.key === "Enter") {
                handleChange()
            }
        },
        [handleChange],
    )

    const checkboxName = `plp-facet-checkbox-${(modal ? "modal-" : "") + v}`
    const labelName = `plp-facet-checkbox-${modal ? "modal-" : ""}label-${v}`
    return (
        <FacetWrapper>
            <FacetFormControl
                control={
                    <FacetCheckbox
                        checked={s ?? false}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        disabled={d}
                        inputProps={
                            {
                                "data-testid": formatTextTestIds(checkboxName),
                                tabIndex: -1,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                    />
                }
                label={
                    <>
                        <span
                            aria-label={`${c} Products`}
                            data-testid={formatTextTestIds(`plp-facet-modal-label-${v}-${c}`)}
                        >
                            {n}
                        </span>

                        {
                            // eslint-disable-next-line react/no-danger
                            <HiddenNoTranslationFacetValue
                                data-testid={formatTextTestIds(`plp-facet-modal-hidden-label-${v}`)}
                                id={formatTextTestIds(`plp-facet-modal-hidden-label-${v}`)}
                                dangerouslySetInnerHTML={noTranslationText(n)}
                            />
                        }
                        {v !== featDeliverBy && (
                            <FacetCountLabel
                                disabled={d}
                                aria-label={`${c} Products`}
                                data-testid={formatTextTestIds(`plp-facet-modal-count-label-${v}-${c}`)}
                            >
                                ({c})
                            </FacetCountLabel>
                        )}
                    </>
                }
                data-testid={formatTextTestIds(labelName)}
                name={checkboxName}
                id={checkboxName}
                value={v}
                tabIndex={0}
                onKeyDown={handleKeyDown}
                ref={innerRef}
            />
            {enabledTooltips && visibleTooltip && (
                <FacetTooltipIcon
                    onClick={handleTooltipOpen}
                    onKeyDown={handleTooltipOpen}
                    data-testid={formatTextTestIds(`plp-facet-tooltip-icon-${v}`)}
                    src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/next-help-question.svg`}
                    alt={`${n} Tooltip Icon`}
                    aria-label={`${v}-facet-tooltip-open`}
                    tabIndex={0}
                />
            )}
            {facetValueTooltipOpen === v && <TooltipComponent handleTooltipClose={handleTooltipClose} facetType={v} />}
        </FacetWrapper>
    )
}

export default connect(Facet)
